import { useCallback, useEffect, useRef, useState } from "react";
import {
  filterByDateRange,
  formatDateIfDate,
  parseDateValue,
} from "../classes/utils/DateUtils";
import { isRequestCancelled } from "../classes/utils/RequestUtils";
import { filterBySearchTerm } from "../classes/utils/SearchUtils";
import { getPatientMorphologyPicturesList } from "../services/ImageService";

export function usePatientImages(patientId) {
  const [patientImages, setPatientImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Always reflects the latest patientId so in-flight fetches can detect staleness
  const latestPatientIdRef = useRef(patientId);
  latestPatientIdRef.current = patientId;

  const fetchPatientImages = useCallback(
    async signal => {
      if (!patientId) return;
      const fetchedForPatientId = patientId;
      setPatientImages([]);
      setLoading(true);
      setError(null);
      try {
        const response = await getPatientMorphologyPicturesList(
          patientId,
          signal
        );
        if (latestPatientIdRef.current !== fetchedForPatientId) return;
        if (response && response.data) {
          const sortedImages = response.data.sort(
            (a, b) =>
              (parseDateValue(b.date)?.getTime() ?? 0) -
              (parseDateValue(a.date)?.getTime() ?? 0)
          );
          setPatientImages(sortedImages);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        if (isRequestCancelled(error)) return;
        if (latestPatientIdRef.current === fetchedForPatientId) {
          setError(error);
        }
      } finally {
        if (latestPatientIdRef.current === fetchedForPatientId) {
          setLoading(false);
        }
      }
    },
    [patientId]
  );

  const addPatientImages = useCallback(newImages => {
    setPatientImages(prevImages =>
      [...newImages, ...prevImages].sort(
        (a, b) =>
          (parseDateValue(b.date)?.getTime() ?? 0) -
          (parseDateValue(a.date)?.getTime() ?? 0)
      )
    );
  }, []);

  const filterPatientImagesByDate = useCallback(
    (startDate, endDate, imagesToFilter = patientImages) => {
      return filterByDateRange(
        imagesToFilter,
        image => image.date,
        startDate,
        endDate
      );
    },
    [patientImages]
  );
  const searchPatientImages = useCallback((searchTerm, dateFilteredImages) => {
    return filterBySearchTerm(
      dateFilteredImages || [],
      image => `${image.title || ""} ${formatDateIfDate(image.date) || ""}`,
      searchTerm
    );
  }, []);

  useEffect(() => {
    // Reset all states when patient changes
    setPatientImages([]);
    setLoading(false);
    setError(null);
  }, [patientId]);

  return {
    patientImages,
    fetchPatientImages,
    addPatientImages,
    filterPatientImagesByDate,
    searchPatientImages,
    loading,
    error,
  };
}
